import * as React from 'react';
import { createContext, useCallback, useContext } from 'react';

import { type ApolloQueryResult, type WatchQueryFetchPolicy, useMutation, useQuery } from '@apollo/client';

import { GoalSettingEventingProvider } from 'bundles/course-v2/components/eventing/GoalSettingEventingProvider';
import EditWeeklyGoal from 'bundles/goal-setting/components/unified-goals/queries/EditWeeklyGoal.graphql';
import GetLearnerWeeklyGoals from 'bundles/goal-setting/components/unified-goals/queries/GetLearnerWeeklyGoals.graphql';
import SetWeeklyGoal from 'bundles/goal-setting/components/unified-goals/queries/SetWeeklyGoal.graphql';
import type {
  EditWeeklyGoalMutation,
  EditWeeklyGoalMutationVariables,
} from 'bundles/goal-setting/components/unified-goals/queries/__generated__/EditWeeklyGoal';
import type {
  GetLearnerWeeklyGoalsQuery as GetLearnerWeeklyGoalsQueryData,
  GetLearnerWeeklyGoalsQueryVariables,
  GetLearnerWeeklyGoals_WeeklyGoalFragment as LearnerGoals,
} from 'bundles/goal-setting/components/unified-goals/queries/__generated__/GetLearnerWeeklyGoals';
import type {
  SetWeeklyGoalMutation,
  SetWeeklyGoalMutationVariables,
} from 'bundles/goal-setting/components/unified-goals/queries/__generated__/SetWeeklyGoal';
import { isUnifiedGoalSettingEnabled } from 'bundles/goal-setting/components/unified-goals/utils/featureGates';

export type UnifiedGoalsContextType = {
  areGoalsLoading: boolean;
  learnerGoals: LearnerGoals | undefined | null;
  saveNewWeeklyGoal: ReturnType<typeof useMutation<SetWeeklyGoalMutation, SetWeeklyGoalMutationVariables>>[0];
  editWeeklyGoal: ReturnType<typeof useMutation<EditWeeklyGoalMutation, EditWeeklyGoalMutationVariables>>[0];
  refetchGoals: (() => Promise<undefined | ApolloQueryResult<GetLearnerWeeklyGoalsQueryData>>) | null;
};

export const UnifiedGoalsContext = createContext<UnifiedGoalsContextType>({
  areGoalsLoading: false,
  learnerGoals: null,
  saveNewWeeklyGoal: () => Promise.resolve({ data: null }),
  editWeeklyGoal: () => Promise.resolve({ data: null }),
  refetchGoals: null,
});

export type UnifiedGoalsDataProviderProps = {
  children: React.ReactNode;
  courseId?: string;
  courseSlug?: string;
  courseName?: string;
  skipEnterpriseQueries?: boolean;
  skipGoalSettingQuery?: boolean;
  ssrGoalQuery?: boolean;
  /**
   * Controls the Apollo fetch policy for the learner goals query.
   */
  fetchPolicy?: WatchQueryFetchPolicy;
};

/**
 * A data provider that provides the learner's weekly goals and the ability to save and edit them.
 * Additionally, it also exposes enterprise eventing data for goal setting via the useGoalSettingEventingData hook which is provided in GoalSettingEventingProvider.
 *
 * @param courseId - The ID of the course.
 * @param courseSlug - The slug of the course.
 * @param courseName - The name of the course.
 * @param skipEnterpriseQueries - If set to true, the queries for fetching the enterprise organization slug used for eventing will be skipped. Defaults to false.
 * @param ssrGoalQuery - If set to true, the query for fetching the learner's weekly goals will be executed on the server side. Defaults to true.
 * @param fetchPolicy - The Apollo fetch policy for the learner goals query.
 * @param children - The child components to render.
 *
 * @returns The UnifiedGoalsContext Provider wrapped by the GoalSettingEventingProvider.
 */
export const UnifiedGoalsDataProvider = ({
  children,
  courseId,
  courseSlug,
  courseName,
  skipEnterpriseQueries,
  skipGoalSettingQuery,
  ssrGoalQuery = true,
  fetchPolicy,
}: UnifiedGoalsDataProviderProps) => {
  const unifiedGoalSettingEnabled = isUnifiedGoalSettingEnabled();

  const {
    loading: areGoalsLoading,
    data,
    refetch: refetchLearnerGoals,
  } = useQuery<GetLearnerWeeklyGoalsQueryData, GetLearnerWeeklyGoalsQueryVariables>(GetLearnerWeeklyGoals, {
    context: { clientName: 'gatewayGql' },
    ssr: ssrGoalQuery,
    skip: !unifiedGoalSettingEnabled || skipGoalSettingQuery,
    fetchPolicy,
  });
  const learnerGoals = data?.LearnerGoals?.queryWeeklyGoal;

  const [saveNewWeeklyGoal] = useMutation<SetWeeklyGoalMutation, SetWeeklyGoalMutationVariables>(SetWeeklyGoal, {
    context: { clientName: 'gatewayGql' },
  });

  const [editWeeklyGoal] = useMutation<EditWeeklyGoalMutation, EditWeeklyGoalMutationVariables>(EditWeeklyGoal, {
    context: { clientName: 'gatewayGql' },
  });

  const refetchGoals = useCallback<() => $TSFixMe>(async () => {
    if (unifiedGoalSettingEnabled) {
      return refetchLearnerGoals?.();
    } else {
      return undefined;
    }
  }, [refetchLearnerGoals, unifiedGoalSettingEnabled]);

  return (
    <GoalSettingEventingProvider
      skipEnterpriseQueries={skipEnterpriseQueries || skipGoalSettingQuery}
      courseId={courseId}
      courseName={courseName}
      courseSlug={courseSlug}
    >
      <UnifiedGoalsContext.Provider
        value={{
          areGoalsLoading,
          learnerGoals,
          saveNewWeeklyGoal,
          editWeeklyGoal,
          refetchGoals,
        }}
      >
        {children}
      </UnifiedGoalsContext.Provider>
    </GoalSettingEventingProvider>
  );
};

/**
 * @description Data provider and hook for accessing learner's goals within the unified goal setting system.
 */
export const useLearnerGoals = () => useContext(UnifiedGoalsContext);
